import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DocumentPreview from '../Preview/DocumentPreview';
import { previewDocument } from '../../services/documents';
import { compareDocuments, getCompareStatus, getCompareResult } from '../../services/docCompare';

const POLL_INTERVAL_MS = 2500;
const ACCEPTED = '.pdf,.docx,.txt,.md,.html,.png,.jpg,.jpeg,.webp,.bmp,.tiff';
const PREVIEWABLE_BACKEND = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.bmp'];
const PREVIEWABLE_TEXT = ['.txt', '.md'];

const PHASE_LABELS = {
  converting: 'Convirtiendo documentos',
  comparing: 'Comparando secciones',
  synthesizing: 'Generando resumen',
};

const ESTADO_STYLES = {
  identica: { label: 'Idéntica', color: '#16a34a', bg: '#dcfce7' },
  equivalente: { label: 'Equivalente', color: '#0369a1', bg: '#e0f2fe' },
  distinta: { label: 'Distinta', color: '#b91c1c', bg: '#fee2e2' },
  solo_a: { label: 'Solo en A', color: '#92400e', bg: '#fef3c7' },
  solo_b: { label: 'Solo en B', color: '#6b21a8', bg: '#f3e8ff' },
};

function extensionOf(name) {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function DocSlot({ tag, file, onSelect }) {
  const [images, setImages] = useState(null);
  const [text, setText] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (selected) => {
    onSelect(selected);
    setImages(null);
    setText(null);
    const ext = extensionOf(selected.name);
    if (PREVIEWABLE_BACKEND.includes(ext)) {
      setLoadingPreview(true);
      try {
        const preview = await previewDocument(selected);
        setImages(preview.images || []);
      } catch {
        setImages(null);
      } finally {
        setLoadingPreview(false);
      }
    } else if (PREVIEWABLE_TEXT.includes(ext)) {
      const reader = new FileReader();
      reader.onload = () => setText(String(reader.result).slice(0, 5000));
      reader.readAsText(selected);
    }
  };

  return (
    <div className="doc-slot">
      <p className="doc-slot-title">Documento {tag}</p>
      {!file ? (
        <div className="doc-drop" onClick={() => inputRef.current.click()}>
          <p>Haz clic para seleccionar</p>
          <p className="doc-drop-hint">PDF, DOCX, TXT, MD, HTML o imagen</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            hidden
            onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
          />
        </div>
      ) : (
        <>
          <p className="doc-filename">{file.name}</p>
          {loadingPreview && <p className="doc-hint">Cargando vista previa...</p>}
          {images && <DocumentPreview images={images} loading={false} metadata={{}} />}
          {text && <pre className="doc-text-preview">{text}</pre>}
          {!loadingPreview && !images && !text && (
            <p className="doc-hint">Sin vista previa para este formato</p>
          )}
        </>
      )}
    </div>
  );
}

function DocCompareView() {
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [taskId, setTaskId] = useState(null);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    return () => clearInterval(pollRef.current);
  }, []);

  const launch = async (userPrompt) => {
    setError(null);
    setResult(null);
    setStatus(null);
    try {
      const { task_id: newTaskId } = await compareDocuments(fileA, fileB, userPrompt);
      setTaskId(newTaskId);
      pollRef.current = setInterval(async () => {
        try {
          const current = await getCompareStatus(newTaskId);
          setStatus(current);
          if (current.state === 'SUCCESS') {
            clearInterval(pollRef.current);
            setResult(await getCompareResult(newTaskId));
          } else if (current.state === 'FAILURE') {
            clearInterval(pollRef.current);
          }
        } catch (err) {
          clearInterval(pollRef.current);
          setError(err.message);
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = () => {
    clearInterval(pollRef.current);
    setFileA(null);
    setFileB(null);
    setPrompt('');
    setTaskId(null);
    setStatus(null);
    setResult(null);
    setError(null);
  };

  const running = taskId && !result && status?.state !== 'FAILURE' && !error;
  const meta = status?.meta || {};

  return (
    <div className="doc-compare-view">
      <div className="doc-slots">
        <DocSlot tag="A" file={fileA} onSelect={setFileA} />
        <DocSlot tag="B" file={fileB} onSelect={setFileB} />
      </div>

      {fileA && fileB && !result && !running && (
        <div className="compare-controls">
          <textarea
            className="compare-prompt"
            placeholder="¿Qué quieres comparar? (ej: fíjate en los plazos de pago y las penalizaciones)"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
          />
          <div className="compare-buttons">
            <button
              className="export-button"
              disabled={!prompt.trim()}
              onClick={() => launch(prompt.trim())}
            >
              Comparar con mi indicación
            </button>
            <button className="save-button" onClick={() => launch(null)}>
              Comparación automática con IA
            </button>
          </div>
        </div>
      )}

      {running && (
        <div className="compare-progress">
          <div className="spinner"></div>
          <p>
            {PHASE_LABELS[meta.phase] || 'Procesando'}
            {meta.total ? ` (${meta.current}/${meta.total})` : ''}
          </p>
        </div>
      )}

      {error && (
        <div className="error-container"><p>{error}</p></div>
      )}
      {status?.state === 'FAILURE' && (
        <div className="error-container"><p>Error en la comparación: {status.error}</p></div>
      )}

      {result && (
        <div className="compare-result">
          <h3>Resultado</h3>
          <div className="compare-summary markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.resumen}</ReactMarkdown>
          </div>
          {result.secciones.length > 0 && <h3>Secciones ({result.secciones.length})</h3>}
          {result.secciones.map((s, i) => {
            const style = ESTADO_STYLES[s.estado] || ESTADO_STYLES.distinta;
            return (
              <details key={i} className="compare-section">
                <summary>
                  <span className="estado-chip" style={{ color: style.color, background: style.bg }}>
                    {style.label}
                  </span>
                  {s.titulo || '(sin título)'}
                </summary>
                <p className="section-detail">{s.detalle}</p>
                {s.cita_a && <blockquote className="cita cita-a"><b>A:</b> {s.cita_a}</blockquote>}
                {s.cita_b && <blockquote className="cita cita-b"><b>B:</b> {s.cita_b}</blockquote>}
              </details>
            );
          })}
        </div>
      )}

      {(fileA || fileB) && (
        <button className="reset-button" onClick={handleReset}>
          Nueva comparación
        </button>
      )}

      <style>{`
        .doc-compare-view {
          max-width: 1100px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .doc-slots {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .doc-slot {
          background: var(--card-background);
          border-radius: var(--border-radius);
          box-shadow: var(--shadow);
          padding: 16px;
          min-height: 200px;
        }
        .doc-slot-title {
          font-weight: 700;
          margin-bottom: 10px;
        }
        .doc-drop {
          border: 2px dashed var(--border-color);
          border-radius: var(--border-radius);
          padding: 40px 16px;
          text-align: center;
          cursor: pointer;
        }
        .doc-drop:hover { border-color: var(--primary-color); }
        .doc-drop-hint { font-size: 12px; color: var(--text-secondary); margin-top: 6px; }
        .doc-filename { font-size: 13px; font-weight: 600; margin-bottom: 10px; word-break: break-all; }
        .doc-hint { font-size: 12px; color: var(--text-secondary); }
        .doc-text-preview {
          font-size: 11px;
          background: #f9fafb;
          padding: 10px;
          border-radius: 6px;
          max-height: 420px;
          overflow: auto;
          white-space: pre-wrap;
        }
        .compare-controls {
          background: var(--card-background);
          border-radius: var(--border-radius);
          box-shadow: var(--shadow);
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .compare-prompt {
          width: 100%;
          padding: 10px;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
        }
        .compare-buttons { display: flex; gap: 12px; }
        .compare-progress {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 24px;
        }
        .compare-result {
          background: var(--card-background);
          border-radius: var(--border-radius);
          box-shadow: var(--shadow);
          padding: 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .compare-summary { font-size: 14px; }
        .markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4 {
          margin: 16px 0 8px;
          font-size: 15px;
          font-weight: 700;
        }
        .markdown-body h3 { font-size: 16px; }
        .markdown-body p { margin: 8px 0; line-height: 1.55; }
        .markdown-body ul, .markdown-body ol { margin: 8px 0; padding-left: 22px; }
        .markdown-body li { margin: 4px 0; line-height: 1.5; }
        .markdown-body code {
          background: #f3f4f6;
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 12px;
        }
        .markdown-body table {
          border-collapse: collapse;
          margin: 12px 0;
          width: 100%;
          font-size: 13px;
          display: block;
          overflow-x: auto;
        }
        .markdown-body th, .markdown-body td {
          border: 1px solid var(--border-color);
          padding: 6px 10px;
          text-align: left;
        }
        .markdown-body th { background: #f9fafb; font-weight: 600; }
        .markdown-body blockquote {
          border-left: 3px solid var(--border-color);
          margin: 8px 0;
          padding: 4px 12px;
          color: var(--text-secondary);
        }
        .markdown-body hr { border: none; border-top: 1px solid var(--border-color); margin: 14px 0; }
        .compare-section {
          border: 1px solid var(--border-color);
          border-radius: 6px;
          padding: 8px 12px;
        }
        .compare-section summary {
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .estado-chip {
          font-size: 11px;
          font-weight: 700;
          padding: 2px 10px;
          border-radius: 999px;
          white-space: nowrap;
        }
        .section-detail { font-size: 13px; margin: 10px 0; }
        .cita {
          font-size: 12px;
          border-left: 3px solid var(--border-color);
          margin: 6px 0;
          padding: 6px 10px;
          background: #f9fafb;
        }
        .cita-a { border-left-color: #92400e; }
        .cita-b { border-left-color: #6b21a8; }
      `}</style>
    </div>
  );
}

export default DocCompareView;
